import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { ToastContainer, toast, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Header from "./components/Header";
import Footer from "./components/Footer";
import HomePage from "./pages/HomePage";
import CollectionPage from "./pages/CollectionPage";
import ProductPage from "./pages/ProductPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import AccountPage, { EmailVerificationPage } from "./pages/AccountPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import TrackOrderPage from "./pages/TrackOrderPage";
import B2BPage from "./pages/B2BPage";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import api from "./services/api";
import FloatingChat from "./pages/FloatingChat";
import ShippingPolicy from "./pages/ShippingPolicy";
import CancellationReturns from "./pages/CancellationReturns";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";

// ─── Scroll to Top Component ───────────────────────────────────────────────
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });
  }, [pathname]);

  return null;
}

// ─── App Context ───────────────────────────────────────────────────────────
const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

// ─── Toast Helper Functions ────────────────────────────────────────────────
const showSuccessToast = (message) => {
  toast.success(message, {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "light",
    transition: Bounce,
  });
};

const showErrorToast = (message) => {
  toast.error(message, {
    position: "top-right",
    autoClose: 4000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "light",
    transition: Bounce,
  });
};

const showInfoToast = (message) => {
  toast.info(message, {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "light",
    transition: Bounce,
  });
};

const showWarningToast = (message) => {
  toast.warning(message, {
    position: "top-right",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "light",
    transition: Bounce,
  });
};

// ─── App Provider ──────────────────────────────────────────────────────────
export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("kkb_cart") || "[]");
    } catch {
      return [];
    }
  });

  const [wishlist, setWishlist] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("kkb_wishlist") || "[]");
    } catch {
      return [];
    }
  });

  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("kkb_token");
    if (!token) {
      setAuthLoading(false);
      return;
    }
    api.auth
      .me()
      .then((u) => {
        setUser(u);
        api.wishlist
          .get()
          .then((d) => {
            setWishlist(d.wishlist || []);
            localStorage.setItem(
              "kkb_wishlist",
              JSON.stringify(d.wishlist || []),
            );
          })
          .catch(() => {});
        api.cart
          .get()
          .then((d) => {
            setCart(d.items || []);
            localStorage.setItem("kkb_cart", JSON.stringify(d.items || []));
          })
          .catch(() => {});
      })
      .catch(() => localStorage.removeItem("kkb_token"))
      .finally(() => setAuthLoading(false));
  }, []);

  useEffect(() => {
    if (!user) localStorage.setItem("kkb_cart", JSON.stringify(cart));
  }, [cart, user]);

  // In App.js - update the login function
  const login = useCallback(async (email, password) => {
    try {
      const data = await api.auth.login({ email, password });
      localStorage.setItem("kkb_token", data.token);

      // ✅ IMPORTANT: Store user data in localStorage
      localStorage.setItem(
        "kkb_user",
        JSON.stringify({
          id: data.id,
          name: data.name,
          email: data.email,
          role: data.role,
          phone: data.phone || "",
        }),
      );

      setUser({
        id: data.id,
        name: data.name,
        email: data.email,
        role: data.role,
        phone: data.phone || "",
      });

      // ... rest of the code remains the same
    } catch (error) {
      showErrorToast(error.message || "Login failed. Please try again.");
      throw error;
    }
  }, []);

  // Update register function too
  const register = useCallback(async (name, email, password, phone) => {
    try {
      const data = await api.auth.register({ name, email, password, phone });
      localStorage.setItem("kkb_token", data.token);

      // ✅ Store user data in localStorage
      localStorage.setItem(
        "kkb_user",
        JSON.stringify({
          id: data.id,
          name: data.name,
          email: data.email,
          role: data.role,
          phone: data.phone || phone,
        }),
      );

      setUser({
        id: data.id,
        name: data.name,
        email: data.email,
        role: data.role,
        phone: data.phone || phone,
      });
      showSuccessToast(`Account created successfully! Welcome ${name}! 🎉`);
      return data;
    } catch (error) {
      showErrorToast(error.message || "Registration failed. Please try again.");
      throw error;
    }
  }, []);

  // Update logout function to clear localStorage
  const logout = useCallback(async () => {
    await api.auth.logout().catch(() => {});
    localStorage.removeItem("kkb_token");
    localStorage.removeItem("kkb_user"); // ✅ Also remove user data
    localStorage.removeItem("kkb_cart");
    localStorage.removeItem("kkb_wishlist");
    setUser(null);
    setCart([]);
    setWishlist([]);
    showInfoToast("You have been logged out. See you soon! 👋");
  }, []);

  const addToCart = useCallback(
    async (
      product,
      qty = 1,
      size = "Regular (2.0m)",
      price = null,
      color = null,
    ) => {
      // Use the provided price or fall back to product.price
      const itemPrice = price !== null ? price : product.price;
      // Get product ID (handle both id and _id)
      const productId = product.id || product._id;
      // Create unique key including color
      const key = `${productId}-${size}-${color || "default"}`;

      try {
        if (user) {
          // If user is logged in, use API
          const data = await api.cart.add(productId, qty, size, color);
          setCart(data.items || []);
        } else {
          // Guest cart
          setCart((prev) => {
            const existing = prev.find((i) => i.key === key);
            if (existing) {
              // Update quantity if item already exists
              return prev.map((i) =>
                i.key === key ? { ...i, qty: i.qty + qty } : i,
              );
            }
            // Add new item
            return [
              ...prev,
              {
                key: key,
                productId: productId,
                name: product.name,
                brand: product.brand,
                image: product.image,
                price: itemPrice,
                qty: qty,
                size: size,
                color: color || null,
              },
            ];
          });
        }
        showSuccessToast(`✨ ${product.name} added to cart!`);
      } catch (err) {
        showErrorToast(err.message || "Failed to add to cart");
      }
    },
    [user],
  );

  const removeFromCart = useCallback(
    async (key) => {
      try {
        if (user) {
          const data = await api.cart.remove(key);
          setCart(data.items || []);
        } else {
          setCart((prev) => prev.filter((i) => i.key !== key));
        }
        showInfoToast("Item removed from cart");
      } catch (err) {
        showErrorToast(err.message || "Failed to remove item");
      }
    },
    [user],
  );

  const updateQty = useCallback(
    async (key, qty) => {
      if (qty < 1) {
        removeFromCart(key);
        return;
      }
      try {
        if (user) {
          const data = await api.cart.update(key, qty);
          setCart(data.items || []);
        } else {
          setCart((prev) =>
            prev.map((i) => (i.key === key ? { ...i, qty } : i)),
          );
        }
      } catch (err) {
        showErrorToast(err.message || "Failed to update quantity");
      }
    },
    [user, removeFromCart],
  );

  const clearCart = useCallback(async () => {
    try {
      if (user) await api.cart.clear();
      setCart([]);
      localStorage.removeItem("kkb_cart");
      showInfoToast("Cart cleared successfully");
    } catch {
      showErrorToast("Failed to clear cart");
    }
  }, [user]);

  const toggleWishlist = useCallback(
    async (productId) => {
      try {
        if (user) {
          const data = await api.wishlist.toggle(productId);
          setWishlist(data.wishlist || []);
          localStorage.setItem(
            "kkb_wishlist",
            JSON.stringify(data.wishlist || []),
          );
          showSuccessToast(
            data.action === "added"
              ? "Added to wishlist"
              : "Removed from wishlist",
          );
        } else {
          setWishlist((prev) => {
            const next = prev.includes(productId)
              ? prev.filter((id) => id !== productId)
              : [...prev, productId];
            localStorage.setItem("kkb_wishlist", JSON.stringify(next));
            showSuccessToast(
              next.includes(productId)
                ? "Added to wishlist"
                : "Removed from wishlist",
            );
            return next;
          });
        }
      } catch (err) {
        showErrorToast(err.message || "Failed to update wishlist");
      }
    },
    [user],
  );

  const cartCount = cart.reduce((s, i) => s + (i.qty || 0), 0);
  const cartTotal = cart.reduce((s, i) => s + (i.price || 0) * (i.qty || 0), 0);

  // Add showToast to the context value
  const showToast = (message, type = "success") => {
    if (type === "success") showSuccessToast(message);
    else if (type === "error") showErrorToast(message);
    else if (type === "info") showInfoToast(message);
    else if (type === "warning") showWarningToast(message);
    else showSuccessToast(message);
  };

  const value = {
    user,
    authLoading,
    login,
    register,
    logout,
    cart,
    cartCount,
    cartTotal,
    addToCart,
    removeFromCart,
    updateQty,
    clearCart,
    wishlist,
    toggleWishlist,
    searchOpen,
    setSearchOpen,
    showToast, // Make sure showToast is included here
  };

  return (
    <AppContext.Provider value={value}>
      {children}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Bounce}
        limit={5}
      />
    </AppContext.Provider>
  );
}

// ─── Main App Routes Component ─────────────────────────────────────────────
function AppRoutes() {
  const location = useLocation();

  // Debug: Log current path
  // useEffect(() => {
  //   console.log("Current path:", location.pathname);
  // }, [location]);

  return (
    <Routes location={location}>
      <Route path="/" element={<HomePage />} />
      <Route
        path="/collection"
        element={<CollectionPage key={location.key} />}
      />
      <Route path="/product/:id" element={<ProductPage />} />
      <Route path="/cart" element={<CartPage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/account" element={<AccountPage />} />
      {/* Make sure this route is correctly defined */}
      <Route path="/verify-email/:token" element={<EmailVerificationPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/track" element={<TrackOrderPage />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/b2b" element={<B2BPage />} />
      <Route path="/shipping-policy" element={<ShippingPolicy />} />
      <Route path="/cancellation-policy" element={<CancellationReturns />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      {/* Add a 404 catch-all route for debugging */}
      <Route
        path="*"
        element={
          <div style={{ padding: "50px", textAlign: "center" }}>
            <h1>404 - Page Not Found</h1>
            <p>The requested path "{location.pathname}" was not found.</p>
            <p>Make sure the route is properly configured.</p>
          </div>
        }
      />
    </Routes>
  );
}

// ─── App Component ─────────────────────────────────────────────────────────
export default function App() {
  return (
    <BrowserRouter>
      <AppProvider>
        <ScrollToTop />
        <FloatingChat />
        <Header />
        <main>
          <AppRoutes />
        </main>
        <Footer />
      </AppProvider>
    </BrowserRouter>
  );
}
